import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import TextureOutlinedIcon from "@mui/icons-material/TextureOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import FileThumbnails from "../../../Estimates/FileThumbnails";
import { getFileContent } from "../../../../api/Design/fileApi";
import { getProductById } from "../../../../api/Design/productApi";
import { getSubgrupos } from "../../../../api/Subgrupos/subgrupoApi";
import {
  drawerAccordionSx,
  drawerAccordionSummarySx,
  outlinedFieldSx,
  sectionChipSx,
} from "../drawerSurfaceSx";
import { useTranslation } from "react-i18next";

function PhotoPreviewDialog({ open, filePath, fileName, onClose }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !filePath) {
      setSrc(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSrc(null);

    getFileContent(filePath)
      .then((response) => {
        if (cancelled) return;
        const file = response?.data;
        if (!file?.data || !file?.mimeType) {
          setError("No se pudo cargar la imagen");
          return;
        }
        setSrc(`data:${file.mimeType};base64,${file.data}`);
      })
      .catch(() => {
        if (!cancelled) setError("No se pudo cargar la imagen");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, filePath]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "rgba(20,20,20,0.96)",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "common.white",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            pr: 1,
          }}
        >
          {fileName || ""}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "common.white" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 280,
        }}
      >
        {loading && <CircularProgress sx={{ color: "common.white" }} />}
        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {!loading && !error && src && (
          <Box
            component="img"
            src={src}
            alt={fileName || "preview"}
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: 1,
              display: "block",
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PhotoStrip({ files, canUpload, onUpload, onDelete, isReadOnly, accent }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState({ open: false, path: null, name: "" });

  const openPreview = (file) => {
    if (!file?.path) return;
    setPreview({
      open: true,
      path: file.path,
      name: file.name || file.path.split(/[/\\]/).pop() || "",
    });
  };

  const closePreview = () => {
    setPreview({ open: false, path: null, name: "" });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!canUpload || isReadOnly) return;

    const dropped = Array.from(e.dataTransfer?.files || []).filter((f) =>
      f.type?.startsWith("image/"),
    );
    if (dropped.length) onUpload(dropped);
  };

  return (
    <>
    <Box
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (canUpload && !isReadOnly) setDragOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (canUpload && !isReadOnly) setDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
      }}
      onDrop={handleDrop}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        flexWrap: "wrap",
        mt: 1.75,
        p: 1.5,
        minHeight: 92,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: dragOver
          ? accent
          : alpha(theme.palette.divider, 0.9),
        bgcolor: dragOver
          ? alpha(accent, 0.1)
          : alpha(accent, 0.04),
        backgroundImage: dragOver
          ? "none"
          : `radial-gradient(circle at 12% 20%, ${alpha(accent, 0.08)} 0%, transparent 45%)`,
        transition: "border-color 0.15s ease, background-color 0.15s ease",
      }}
    >
      {files.map((file, i) => (
        <Box
          key={`${file.path || file.name}-${i}`}
          role={file.path ? "button" : undefined}
          tabIndex={file.path ? 0 : undefined}
          onClick={() => openPreview(file)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPreview(file);
            }
          }}
          sx={{
            position: "relative",
            width: 72,
            height: 72,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.85),
            bgcolor: theme.palette.background.paper,
            boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.08)}`,
            cursor: file.path ? "zoom-in" : "default",
            "&:hover .photo-actions": { opacity: 1 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              "& img, & canvas, & > *": {
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "cover",
              },
            }}
          >
            <FileThumbnails filePath={file.path} />
          </Box>
          <Box
            className="photo-actions"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-end",
              gap: 0.2,
              p: 0.4,
              bgcolor: "rgba(0,0,0,0.38)",
              opacity: 0,
              transition: "opacity 0.15s ease",
              pointerEvents: "none",
            }}
          >
            {file.path && (
              <IconButton
                size="small"
                component="a"
                href={`${process.env.REACT_APP_API_URL}/api/files/readfile?path=${encodeURIComponent(file.path)}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  p: 0.35,
                  pointerEvents: "auto",
                  bgcolor: "rgba(255,255,255,0.92)",
                  "&:hover": { bgcolor: "common.white" },
                }}
              >
                <DownloadIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
            {!isReadOnly && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(file);
                }}
                sx={{
                  p: 0.35,
                  pointerEvents: "auto",
                  bgcolor: "rgba(255,255,255,0.92)",
                  "&:hover": { bgcolor: "common.white" },
                }}
              >
                <DeleteOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}

      {canUpload && !isReadOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={onUpload}
          />
          <Tooltip title={t("Samples.dropOrUploadPhoto")}>
            <Box
              component="button"
              type="button"
              onClick={() => inputRef.current?.click()}
              sx={{
                width: files.length === 0 ? "100%" : 72,
                minHeight: 72,
                flex: files.length === 0 ? "1 1 180px" : "0 0 auto",
                borderRadius: 2,
                border: "1px solid",
                borderColor: dragOver
                  ? accent
                  : alpha(accent, 0.35),
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                color: accent,
                display: "flex",
                flexDirection: files.length === 0 ? "row" : "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                cursor: "pointer",
                px: files.length === 0 ? 1.5 : 0.5,
                py: 1,
                transition: "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease",
                "&:hover": {
                  borderColor: accent,
                  bgcolor: alpha(accent, 0.08),
                  transform: "translateY(-1px)",
                },
              }}
            >
              <AddPhotoAlternateOutlinedIcon fontSize="small" />
              {files.length === 0 ? (
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {t("Samples.dropOrUploadPhoto")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                  >
                    {t("Samples.dropPhotosHint")}
                  </Typography>
                </Box>
              ) : (
                <UploadIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
          </Tooltip>
        </>
      )}

      {!canUpload && (
        <Typography variant="caption" color="text.secondary">
          {t("Samples.saveFirstToUploadPhotos")}
        </Typography>
      )}

      {canUpload && files.length === 0 && isReadOnly && (
        <Typography variant="caption" color="text.secondary">
          {t("Samples.noPhotos")}
        </Typography>
      )}
    </Box>

    <PhotoPreviewDialog
      open={preview.open}
      filePath={preview.path}
      fileName={preview.name}
      onClose={closePreview}
    />
    </>
  );
}

function CategoryBlock({
  title,
  accent,
  icon,
  nameLabel,
  nameValue,
  onNameChange,
  statusLabel,
  statusValue,
  onStatusChange,
  referenciaLabel,
  referenciaValue,
  onReferenciaChange,
  materialLabel,
  materialValue,
  files,
  canUpload,
  onUpload,
  onDelete,
  isReadOnly,
  fin,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const showReferencia = typeof onReferenciaChange === "function";
  const showMaterial = materialLabel != null;

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 1.75 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        bgcolor: theme.palette.background.paper,
        background: `linear-gradient(165deg, ${alpha(accent, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 42%)`,
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 8px 24px ${alpha("#000", 0.25)}`
            : `0 8px 24px ${alpha(accent, 0.08)}`,
        minWidth: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accent, 0.14),
              color: accent,
              border: `1px solid ${alpha(accent, 0.28)}`,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box minWidth={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.9rem",
                lineHeight: 1.2,
                letterSpacing: 0.15,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {files.length} foto{files.length === 1 ? "" : "s"}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Stack spacing={1.75} sx={{ flex: 1 }}>
        <TextField
          size="small"
          label={nameLabel}
          value={nameValue}
          onChange={onNameChange}
          disabled={isReadOnly}
          fullWidth
          sx={fin}
        />
        {showReferencia && (
          <TextField
            size="small"
            label={referenciaLabel}
            value={referenciaValue}
            onChange={onReferenciaChange}
            disabled={isReadOnly}
            fullWidth
            sx={fin}
          />
        )}
        {showMaterial && (
          <TextField
            size="small"
            label={materialLabel}
            value={materialValue || ""}
            disabled
            fullWidth
            sx={fin}
            helperText={
              materialValue
                ? undefined
                : t("Samples.materialFromMainFabric")
            }
          />
        )}
        <TextField
          size="small"
          label={statusLabel}
          value={statusValue}
          onChange={onStatusChange}
          disabled={isReadOnly}
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          sx={fin}
        />
      </Stack>

      <PhotoStrip
        files={files}
        canUpload={canUpload}
        onUpload={onUpload}
        onDelete={onDelete}
        isReadOnly={isReadOnly}
        accent={accent}
      />
    </Box>
  );
}

export default function CalidadesPrintSection({
  muestra,
  setMuestra,
  isReadOnly,
  selectedProduct = null,
  calidadesFiles = [],
  printFiles = [],
  onUploadCalidades,
  onUploadPrint,
  onDeleteCalidades,
  onDeletePrint,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const accSx = drawerAccordionSx(theme);
  const sumSx = drawerAccordionSummarySx(theme);
  const fin = outlinedFieldSx(theme);
  const chipSx = sectionChipSx(theme);
  const canUpload = Boolean(muestra?.ID_MUESTRA);
  const photoCount = (calidadesFiles?.length || 0) + (printFiles?.length || 0);

  const productId =
    selectedProduct?.id ??
    selectedProduct?.ProductId ??
    selectedProduct?.productId ??
    selectedProduct?.ID_PRODUCTO ??
    muestra?.ID_PRODUCTO ??
    null;

  // Material = nombre del Main Fabric del producto (solo lectura)
  useEffect(() => {
    let cancelled = false;

    const resolveMainFabricName = async () => {
      const fromProduct =
        selectedProduct?.main_fabric_name ||
        selectedProduct?.mainFabricName ||
        null;

      let mainFabricId =
        selectedProduct?.main_fabric ??
        selectedProduct?.ID_SUBGRUPO ??
        null;

      try {
        if (!fromProduct && productId) {
          const product = await getProductById(productId);
          if (cancelled) return;
          if (product?.main_fabric != null && product.main_fabric !== "") {
            mainFabricId = product.main_fabric;
          }
        }

        let materialName = fromProduct;
        if (!materialName && mainFabricId != null && mainFabricId !== "") {
          const subgrupos = await getSubgrupos();
          if (cancelled) return;
          const match = (subgrupos || []).find(
            (s) => String(s.ID_SUBGRUPO) === String(mainFabricId),
          );
          materialName = match?.SUBGRUPO || "";
        }

        if (cancelled || !materialName) return;

        setMuestra((prev) => {
          if (!prev || prev.CALIDAD_MATERIAL === materialName) return prev;
          return { ...prev, CALIDAD_MATERIAL: materialName };
        });
      } catch (err) {
        console.error("Error resolviendo Main Fabric para Calidades:", err);
      }
    };

    resolveMainFabricName();
    return () => {
      cancelled = true;
    };
  }, [productId, selectedProduct?.main_fabric, selectedProduct?.main_fabric_name, setMuestra]);

  return (
    <Accordion defaultExpanded={false} sx={accSx}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", mr: 0.5 }}>
          {t("Samples.calidadesAndPrint")}
        </Typography>
        <Chip
          size="small"
          label={`${photoCount} foto${photoCount === 1 ? "" : "s"}`}
          sx={chipSx}
          onClick={(e) => e.stopPropagation()}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <CategoryBlock
            title={t("Samples.calidades")}
            accent={theme.palette.primary.main}
            icon={<TextureOutlinedIcon fontSize="small" />}
            nameLabel={t("Samples.name")}
            nameValue={muestra.CALIDAD_NOMBRE || ""}
            onNameChange={(e) =>
              setMuestra((prev) => ({
                ...prev,
                CALIDAD_NOMBRE: e.target.value,
              }))
            }
            referenciaLabel={t("Samples.referencia")}
            referenciaValue={muestra.CALIDAD_REFERENCIA || ""}
            onReferenciaChange={(e) =>
              setMuestra((prev) => ({
                ...prev,
                CALIDAD_REFERENCIA: e.target.value,
              }))
            }
            materialLabel={t("Samples.material")}
            materialValue={muestra.CALIDAD_MATERIAL || ""}
            statusLabel={t("Samples.Status")}
            statusValue={muestra.CALIDAD_ESTADO || ""}
            onStatusChange={(e) =>
              setMuestra((prev) => ({
                ...prev,
                CALIDAD_ESTADO: e.target.value || null,
              }))
            }
            files={calidadesFiles}
            canUpload={canUpload}
            onUpload={onUploadCalidades}
            onDelete={onDeleteCalidades}
            isReadOnly={isReadOnly}
            fin={fin}
          />

          <CategoryBlock
            title={t("Samples.print")}
            accent={theme.palette.secondary.main}
            icon={<PrintOutlinedIcon fontSize="small" />}
            nameLabel={t("Samples.name")}
            nameValue={muestra.PRINT_NOMBRE || ""}
            onNameChange={(e) =>
              setMuestra((prev) => ({
                ...prev,
                PRINT_NOMBRE: e.target.value,
              }))
            }
            statusLabel={t("Samples.Status")}
            statusValue={muestra.PRINT_ESTADO || ""}
            onStatusChange={(e) =>
              setMuestra((prev) => ({
                ...prev,
                PRINT_ESTADO: e.target.value || null,
              }))
            }
            files={printFiles}
            canUpload={canUpload}
            onUpload={onUploadPrint}
            onDelete={onDeletePrint}
            isReadOnly={isReadOnly}
            fin={fin}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
